import { useStyletron } from "baseui";
import {
  Input as BaseInput,
  InputProps,
  MaskedInput as BaseMaskedInput,
  MaskedInputProps,
} from "baseui/input";
import React from "react";
import { ChangeEvent } from "react";
import { Controller, UseControllerProps } from "react-hook-form";
import { StyleObject } from "styletron-react";

type Props = {
  $style?: StyleObject;
  masked?: boolean;
  maskChar?: string;
} & InputProps &
  MaskedInputProps;

const Input = ({
  $style,
  maskChar,
  masked,
  ...rest
}: Props): React.ReactElement => {
  const [, theme] = useStyletron();
  const Component = masked ? BaseMaskedInput : BaseInput;

  return (
    <Component
      overrides={{
        Root: {
          style: () => ({
            borderTopLeftRadius: theme.borders.radius300,
            borderTopRightRadius: theme.borders.radius300,
            borderBottomRightRadius: theme.borders.radius300,
            borderBottomLeftRadius: theme.borders.radius300,
            paddingRight: "5px",
            appearance: "textfield",
          }),
        },
        Input: {
          style: {
            backgroundColor: "transparent",
            ":-webkit-autofill": {
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
            },
            paddingRight: 0,
            ...$style,
          },
        },
        EndEnhancer: {
          style: {
            fontSize: "12px",
            marginRight: 0,
          },
        },
      }}
      {...rest}
      maskChar={maskChar}
    />
  );
};

export default Input;

export function ControlledInput({
  control,
  name,
  rules,
  type,
  ...rest
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
UseControllerProps<any> & Props): React.ReactElement {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, onBlur, value } }) => (
        <Input
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            type === "number"
              ? onChange(
                  !!parseFloat(e.target.value) &&
                    Math.abs(parseFloat(e.target.value)) >= 0
                    ? Math.abs(parseFloat(e.target.value))
                    : null
                )
              : onChange(e.target.value)
          }
          onBlur={onBlur}
          value={value}
          type={type}
          {...rest}
        />
      )}
    />
  );
}
